import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CodeIcon, CopyIcon } from "./components/Icons";
import { SearchForm } from "./components/SearchForm";
import { Summary } from "./components/Summary";
import { DataExplorer } from "./components/DataExplorer";
import { RawJsonModal } from "./components/RawJsonModal";
import { formatCnpj, sanitizeCnpj, validateCnpj } from "./lib/cnpj";
import { countFields } from "./lib/json";
import { CnpjApiError, fetchCnpj } from "./services/cnpjApi";
import type { JsonObject } from "./types/json";

const EXAMPLE_CNPJ = "27865757000102";
const PORTAL_URL = "https://www.portalgsemp.com.br";

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  document.execCommand("copy");
  textArea.remove();
}

export default function App() {
  const [input, setInput] = useState("");
  const [data, setData] = useState<JsonObject | null>(null);
  const [loading, setLoading] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string>();
  const [requestError, setRequestError] = useState<string>();
  const [showRawJson, setShowRawJson] = useState(false);
  const [copied, setCopied] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const resultsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const cnpjFromUrl = new URLSearchParams(window.location.search).get("cnpj");
    if (cnpjFromUrl) setInput(formatCnpj(cnpjFromUrl));

    return () => abortControllerRef.current?.abort();
  }, []);

  const fieldCount = useMemo(() => (data ? countFields(data) : { filled: 0, total: 0 }), [data]);

  const handleInputChange = (value: string) => {
    setInput(formatCnpj(value));
    setValidationMessage(undefined);
    setRequestError(undefined);
  };

  const handleSearch = async () => {
    const cleanCnpj = sanitizeCnpj(input);
    const validation = validateCnpj(cleanCnpj);

    if (!validation.valid) {
      setValidationMessage(validation.message);
      setData(null);
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setValidationMessage(undefined);
    setRequestError(undefined);
    setCopied(false);

    try {
      const response = await fetchCnpj(cleanCnpj, controller.signal);
      setData(response);

      const url = new URL(window.location.href);
      url.searchParams.set("cnpj", cleanCnpj);
      window.history.replaceState({}, "", url);

      window.requestAnimationFrame(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      setData(null);
      setRequestError(error instanceof CnpjApiError ? error.message : "Ocorreu um erro inesperado.");
    } finally {
      if (abortControllerRef.current === controller) {
        setLoading(false);
        abortControllerRef.current = null;
      }
    }
  };

  const handleCopyJson = useCallback(async () => {
    if (!data) return;
    await copyText(JSON.stringify(data, null, 2));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2200);
  }, [data]);

  return (
    <div className="container">
      <header className="site-header">
        <a className="brand-link" href={PORTAL_URL} aria-label="GSEMP — Portal GSEMP">
          <img
            className="brand-logo"
            src="/gsemp-logo-branco.png"
            alt="GSEMP — Grupo Solução Empreendimentos"
            width={750}
            height={141}
          />
        </a>
        <nav className="nav" aria-label="Navegação principal">
          <a href={`${PORTAL_URL}/#solucoes`}>Soluções</a>
          <a href={PORTAL_URL}>Portal GSEMP</a>
          <a className="nav-cta" href={PORTAL_URL}>
            Fale com a GSEMP
          </a>
        </nav>
      </header>

      <main>
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-copy">
            <span className="kicker">
              <i aria-hidden="true" />
              Consulta CNPJ gratuita
            </span>
            <h1 id="hero-title">Consulte um CNPJ. Entenda a empresa. Decida melhor.</h1>
            <p>
              Informações empresariais públicas organizadas com clareza: situação cadastral, endereço, atividade
              principal, sócios e o retorno completo da consulta. Sem cadastro e sem precisar passar pelo site do
              governo.
            </p>
            <div className="hero-benefits">
              <span>
                <b>✓</b> Gratuita
              </span>
              <span>
                <b>✓</b> Sem cadastro
              </span>
              <span>
                <b>✓</b> Dados públicos
              </span>
              <span>
                <b>✓</b> Resultado na hora
              </span>
            </div>
          </div>

          <SearchForm
            value={input}
            loading={loading}
            validationMessage={validationMessage}
            requestError={requestError}
            onChange={handleInputChange}
            onSubmit={handleSearch}
            onUseExample={() => {
              setInput(formatCnpj(EXAMPLE_CNPJ));
              setValidationMessage(undefined);
              setRequestError(undefined);
            }}
          />
        </section>

        <div ref={resultsRef} className="scroll-mt-4">
          {data ? (
            <div className="mt-8 space-y-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#1c5a95]">
                    Resultado da consulta
                  </p>
                  <p className="mt-1 text-sm text-[#6f7f94]">
                    {fieldCount.filled.toLocaleString("pt-BR")} de {fieldCount.total.toLocaleString("pt-BR")} campos
                    preenchidos
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setShowRawJson(true)}
                    className="button-secondary rounded-xl px-3.5 py-2.5 text-sm"
                  >
                    <span className="inline-flex items-center gap-2">
                      <CodeIcon className="size-4" /> Ver JSON bruto
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={handleCopyJson}
                    className="button-secondary rounded-xl px-3.5 py-2.5 text-sm"
                  >
                    <span className="inline-flex items-center gap-2">
                      <CopyIcon className="size-4" /> {copied ? "JSON copiado" : "Copiar JSON"}
                    </span>
                  </button>
                </div>
              </div>

              <Summary data={data} />
              <DataExplorer data={data} />
            </div>
          ) : null}
        </div>
      </main>

      <footer className="site-footer">
        <p className="m-0">
          Dados públicos fornecidos por APIs de terceiros (CNPJ.ws e BrasilAPI), sujeitos a atualizações e
          indisponibilidades.
        </p>
        <p className="m-0">
          <a href={PORTAL_URL} className="font-bold text-[#1c5a95] no-underline hover:underline">
            GSEMP · Grupo Solução Empreendimentos
          </a>
        </p>
      </footer>

      {data && showRawJson ? (
        <RawJsonModal
          data={data}
          copied={copied}
          onCopy={handleCopyJson}
          onClose={() => setShowRawJson(false)}
        />
      ) : null}
    </div>
  );
}
